import { resolveStaffCreateRoute } from '@monolith/components/pages/staff-multi-editor/staffMultiEditorHelpers';

import {
    NavigationItem,
    NavigationSection,
} from '../../../packages/navigation-shell';

type LegacyNavigationOptions = {
    businessName: string;
    canGoToInvoiceReporting?: boolean;
    canGoToTransactionReporting?: boolean;
    canUpdateSuperuserTools?: boolean;
    includeSuperuserTools?: boolean;
    mainAppUrl: string;
};

const createExternalItem = (
    mainAppUrl: string,
    id: string,
    label: string,
    path: string,
): NavigationItem => ({
    id,
    label,
    target: {
        kind: 'external',
        href: `${mainAppUrl}${path}`,
    },
});

export const createLegacyNavigation = ({
    businessName,
    canGoToInvoiceReporting = false,
    canGoToTransactionReporting = false,
    canUpdateSuperuserTools = false,
    includeSuperuserTools = false,
    mainAppUrl: suppliedMainAppUrl,
}: LegacyNavigationOptions): NavigationSection[] => {
    const mainAppUrl = suppliedMainAppUrl.replace(/\/$/, '');
    const item = (id: string, label: string, path: string): NavigationItem =>
        createExternalItem(mainAppUrl, id, label, path);

    return [
        {
            id: 'legacy-menu',
            label: 'Menu',
            items: [
                {
                    id: 'schedule',
                    label: 'Schedule',
                    icon: 'date_range',
                    children: [
                        item(
                            'schedule-day-room',
                            'Day View - By Room',
                            '/calendar?view=day&resource=room',
                        ),
                        item(
                            'schedule-day-staff',
                            'Day View - By Staff',
                            '/calendar?view=day&resource=staff',
                        ),
                        item(
                            'schedule-day-service',
                            'Day View - By Service',
                            '/calendar?view=day&resource=service',
                        ),
                        item(
                            'schedule-week',
                            'Week View',
                            '/calendar?view=week',
                        ),
                        item(
                            'schedule-month',
                            'Month View',
                            '/calendar?view=month',
                        ),
                    ],
                },
                {
                    id: 'clients',
                    label: 'Clients',
                    icon: 'account_circle',
                    children: [
                        item('all-clients', 'All Clients', '/clients'),
                        item('prospects', 'Prospects', '/prospects'),
                        item(
                            'prospects-conversion',
                            'Prospects Conversion',
                            '/stats/prospect-conversion',
                        ),
                        item('gift-cards', 'Gift Cards', '/gift-cards'),
                        item('add-client', 'Add client...', '/clients/create'),
                        item(
                            'instant-messaging',
                            'Instant Messaging',
                            '/messaging',
                        ),
                        item('calls', 'Calls', '/calls'),
                        item('proposals', 'Proposals', '/proposals'),
                        item(
                            'waitlist-notification',
                            'Waitlist Notification',
                            '/waitlistnotification',
                        ),
                    ],
                },
                {
                    id: 'staff',
                    label: 'Staff',
                    icon: 'people',
                    children: [
                        item('all-staff', 'All Staff', '/staff'),
                        item('staff-payroll', 'Payroll', '/staff/payroll'),
                        item('staff-time-off', 'Time Off', '/timeoff'),
                        item(
                            'staff-commissions',
                            'Commissions',
                            '/staff/commissions',
                        ),
                        item(
                            'add-staff',
                            'Add Staff...',
                            resolveStaffCreateRoute({ name: businessName }),
                        ),
                    ],
                },
                {
                    id: 'settings',
                    label: 'Settings',
                    icon: 'settings',
                    children: [
                        item(
                            'business-settings',
                            'Business',
                            `/business/${businessName}/edit`,
                        ),
                        item('locations', 'Locations / Rooms', '/locations'),
                        item('services', 'Services', '/services'),
                        item(
                            'terms-and-conditions',
                            'T&C Policies',
                            '/terms-and-conditions-policies',
                        ),
                        item('campaigns', 'Campaigns', '/campaigns'),
                        item('templates', 'Templates', '/templates'),
                        item('promotions', 'Promotions', '/promo'),
                    ],
                },
                {
                    id: 'stats',
                    label: 'Stats',
                    icon: 'insert_chart',
                    children: [
                        item('stats-dashboard', 'Dashboard', '/dashboard'),
                        item('stats-today', 'Today', '/today'),
                        item(
                            'membership-conversions',
                            'Membership Conversions',
                            '/stats/conversions',
                        ),
                        item(
                            'group-class-enrollment',
                            'Group Class Enrollment',
                            '/stats/session-enrollment',
                        ),
                        item('stats-staff', 'Staff', '/stats/staff'),
                        item(
                            'location-utilization',
                            'Location/Room Utilization',
                            '/stats/location',
                        ),
                        item('retention', 'Retention', '/stats/retention'),
                    ],
                },
                {
                    id: 'reporting',
                    label: 'Reporting',
                    icon: 'view_quilt',
                    children: [
                        item(
                            'attendance-report',
                            'Attendance',
                            '/reporting/attendance',
                        ),
                        item(
                            'credits-report',
                            'Credits',
                            '/reporting/credits/',
                        ),
                        ...(canGoToInvoiceReporting
                            ? [
                                  item(
                                      'invoices-report',
                                      'Invoices',
                                      '/reporting/invoices',
                                  ),
                              ]
                            : []),
                        item(
                            'overdue-invoices-report',
                            'Overdue Invoices',
                            '/reporting/open-invoices',
                        ),
                        item(
                            'subscriptions-report',
                            'Subscriptions',
                            '/reporting/subscriptions/',
                        ),
                        ...(canGoToTransactionReporting
                            ? [
                                  item(
                                      'transactions-report',
                                      'Transactions',
                                      '/reporting/transactions',
                                  ),
                              ]
                            : []),
                    ],
                },
                ...(includeSuperuserTools
                    ? [
                          {
                              id: 'superuser-tools',
                              label: 'Superuser tools',
                              icon: 'lock',
                              children: [
                                  item(
                                      'all-businesses',
                                      'All businesses',
                                      '/business',
                                  ),
                                  ...(canUpdateSuperuserTools
                                      ? [
                                            item(
                                                'import-clients',
                                                'Import clients...',
                                                '/clients/import',
                                            ),
                                            item(
                                                'import-appointments',
                                                'Import appointments...',
                                                '/schedule/appointment/import',
                                            ),
                                            item(
                                                'import-class-enrollments',
                                                'Import class enrollm...',
                                                '/schedule/session/import',
                                            ),
                                            item(
                                                'import-credits',
                                                'Import credits...',
                                                '/credits/import',
                                            ),
                                            item(
                                                'import-subscriptions',
                                                'Import subscriptions...',
                                                '/subscription/import',
                                            ),
                                            item(
                                                'import-services',
                                                'Import services...',
                                                '/service/import',
                                            ),
                                            item(
                                                'group-class-creator',
                                                'Group Class creator...',
                                                '/session/creator',
                                            ),
                                            item(
                                                'map-credit-cards',
                                                'Map credit cards...',
                                                '/cc/mapping',
                                            ),
                                            item(
                                                'business-cleanup',
                                                'Clean up',
                                                '/business/cleanup',
                                            ),
                                            item(
                                                'photo-release-migration',
                                                'Photo release migration',
                                                '/business/migrations/photo-release',
                                            ),
                                        ]
                                      : []),
                                  item(
                                      'activities',
                                      'Activities',
                                      '/activities',
                                  ),
                                  item('audit', 'Audit', '/audit'),
                                  ...(canUpdateSuperuserTools
                                      ? [
                                            item(
                                                'recalculate-invoices',
                                                'Recalculate Invoices',
                                                '/invoice/recalculate-total',
                                            ),
                                        ]
                                      : []),
                              ],
                          },
                      ]
                    : []),
            ],
        },
    ];
};
